import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useDeleteReadingTemplate } from '../../hooks/useReadingTemplates';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteReadingTemplateModal = ({ isOpen, onClose, template }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteTemplate = useDeleteReadingTemplate();
    const { t } = useLanguage();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);

        try {
            await deleteTemplate.mutateAsync({ uid: template.uid });
            onClose();
        } catch (error) {
            console.error('Error deleting reading template:', error);
            setDeleteError(error.message || t(KEYS.READING_TEMPLATE_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.READING_TEMPLATE_DELETE_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.COMMON_DELETING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete template <strong>{template?.label}</strong>?
                    </p>
                </div>

                {template && (template.unit || template.value != null) && (
                    <div className="bg-surface-overlay rounded border border-surface-border p-4 space-y-2">
                        {template.value != null && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-muted">Default Value</span>
                                <span className="text-sm font-mono text-text-primary">
                                    {template.value}{template.unit ? ` ${template.unit}` : ''}
                                </span>
                            </div>
                        )}
                        {template.unit && template.value == null && (
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-text-muted">Unit</span>
                                <span className="text-sm font-mono text-text-primary">{template.unit}</span>
                            </div>
                        )}
                    </div>
                )}

                <p className="text-sm text-text-muted">
                    This template will be permanently removed from the system.
                </p>
            </div>
        </Modal>
    );
};

export default DeleteReadingTemplateModal;
